import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

// Selects the primary action button based on canonical status (R4 AC 1–10).
// "Pause" is never offered (R4 AC 11). For reactivate, the button disables
// when grace expires and swaps copy to point to the paywall.
export default function BillingModalPrimaryAction({ summary, onAction }) {
  const status = summary?.status;
  const proExpiresAt = summary?.nextEventDate;
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (status !== 'cancelled') return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [status]);

  if (!status || !summary?.eligible) return null;

  // For a `cancelled` sub, treat both NULL pro_expires_at AND a past timestamp
  // as "grace ended". NULL happens after the cron `check_subscription_expiry`
  // sweeps a cancelled+expired user (clears pro_expires_at) or when cooling-off
  // refund set it to now() which the cron then nulls — without this, the modal
  // renders "Reactivate subscription" which billing-action 422s with
  // `grace_window_expired`, producing a click-to-error path.
  const graceExpired = status === 'cancelled' && (!proExpiresAt || new Date(proExpiresAt).getTime() <= now);

  // Compare like-for-like for the "Switch to annual (save ~X%)" label:
  // `alternatePlanPrice` is the catalogue price of the OTHER interval, so the
  // monthly side must also use the catalogue (priceFull) when the subscription
  // has an active discount applied — otherwise a Founder's Rate monthly user
  // would compare £4.99×12 vs the catalogue annual and the percentage would
  // misrepresent the SKU-vs-SKU saving.
  const planPriceMinor =
    summary?.plan?.priceFull?.amountRaw ?? summary?.plan?.price?.amountRaw;

  const map = primaryForStatus(status, {
    graceExpired,
    interval: summary?.plan?.interval,
    planPriceMinor,
    alternatePriceMinor: summary?.alternatePlanPrice?.amountRaw,
  });
  if (!map) return null;

  const primaryIsLink = map.tone === 'link';

  return (
    <View style={styles.root}>
      <Pressable
        accessibilityRole="button"
        onPress={() => onAction(map.action)}
        style={({ pressed }) => [
          primaryIsLink ? styles.link : [styles.btn, map.tone === 'primary' ? styles.primary : styles.secondary],
          pressed && { opacity: 0.7 },
        ]}
        disabled={map.disabled}
      >
        <Text style={primaryIsLink ? styles.linkText : [styles.btnText, map.tone === 'primary' ? styles.primaryText : styles.secondaryText]}>
          {map.label}
        </Text>
      </Pressable>
      {map.secondary && (
        <Pressable
          accessibilityRole="button"
          onPress={() => onAction(map.secondary.action)}
          style={({ pressed }) => [
            map.secondary.tone === 'link' ? styles.link : [styles.btn, styles.secondary],
            pressed && { opacity: 0.7 },
          ]}
        >
          <Text style={map.secondary.tone === 'link' ? styles.linkText : [styles.btnText, styles.secondaryText]}>
            {map.secondary.label}
          </Text>
        </Pressable>
      )}
    </View>
  );
}

function primaryForStatus(status, { graceExpired, interval, planPriceMinor, alternatePriceMinor }) {
  switch (status) {
    case 'active':
      // Paddle does not allow scheduling a billing-cycle change, so we only
      // expose "Switch plan" for monthly subscribers (upgrade to annual). On
      // annual, the only available action is Cancel; the user can re-subscribe
      // monthly after the annual period ends if they want to change cadence.
      if (interval === 'annual') {
        // Annual users: only cancel is available (no plan switch). Render as
        // a subtle link to match the retention-flow entry style we use
        // elsewhere (MembershipSection's old cancel link).
        return { action: 'cancel_confirm', label: 'Cancel subscription', tone: 'link', asLink: true };
      }
      return {
        action: 'switch_preview',
        label: buildSwitchToAnnualLabel(planPriceMinor, alternatePriceMinor),
        tone: 'primary',
        secondary: { action: 'cancel_confirm', label: 'Cancel subscription', tone: 'link' },
      };
    case 'past_due':
      return {
        action: 'update_card',
        label: 'Update payment method',
        tone: 'primary',
        secondary: { action: 'cancel_confirm', label: 'Cancel subscription', tone: 'link' },
      };
    case 'paused':
      return {
        action: 'resume',
        label: 'Resume subscription',
        tone: 'primary',
        secondary: { action: 'cancel_confirm', label: 'Cancel immediately', tone: 'link' },
      };
    case 'cancelled':
      if (graceExpired) {
        return { action: 'go_to_paywall', label: 'Grace ended, upgrade to Pro', tone: 'primary' };
      }
      return { action: 'reactivate', label: 'Reactivate subscription', tone: 'primary' };
    case 'trial_carded':
      // Step 8: a carded user manages plan changes from /pricing (the switch
      // hub), not in-modal. The primary action sends them to Compare Plans; the
      // actual Monthly<->Annual switch and the carded->CBP conversion are
      // initiated from /pricing (which reuses the billing-action carded branches
      // via the in-page switch preview). Cancel stays available here.
      return {
        action: 'compare_plans',
        label: 'Compare Plans',
        tone: 'primary',
        secondary: { action: 'cancel_confirm', label: 'Cancel subscription', tone: 'link' },
      };
    case 'trial_active':
      return { action: 'cancel_confirm', label: 'End trial', tone: 'secondary' };
    case 'trial_expired':
    case 'grandfather_expired':
      return { action: 'go_to_paywall', label: 'Upgrade to Pro', tone: 'primary' };
    case 'grandfather_active':
      return null; // view-only
    case 'cbp_only':
      return { action: 'go_to_paywall', label: 'Upgrade to Pro', tone: 'primary' };
    default:
      return null;
  }
}

function buildSwitchToAnnualLabel(monthlyMinor, annualMinor) {
  if (
    typeof monthlyMinor !== 'number' || monthlyMinor <= 0 ||
    typeof annualMinor !== 'number' || annualMinor <= 0
  ) {
    return 'Switch to annual';
  }
  const monthlyYearly = monthlyMinor * 12;
  const savingsPct = Math.round(((monthlyYearly - annualMinor) / monthlyYearly) * 100);
  if (savingsPct <= 0) return 'Switch to annual';
  return `Switch to annual (save ~${savingsPct}%)`;
}

const styles = StyleSheet.create({
  root: { paddingHorizontal: 20, paddingVertical: 14, gap: 10, alignItems: 'center' },
  btn: { paddingVertical: 14, borderRadius: 10, alignItems: 'center', alignSelf: 'stretch' },
  primary: { backgroundColor: '#0556F7' },
  secondary: { backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)' },
  btnText: { fontSize: 15, fontWeight: '600' },
  primaryText: { color: '#FFFFFF' },
  secondaryText: { color: '#FFFFFF' },
  link: { paddingVertical: 8, alignItems: 'center' },
  linkText: { fontSize: 12, fontFamily: 'Satoshi-Medium', color: 'rgba(255,255,255,0.45)', textDecorationLine: 'underline' },
});
